import React from "react";
import { Flame, Phone } from "lucide-react";
import fireplaceGif from "../assets/fireplace-burning-fire-animated-gif.gif";
import { ServiceDefinitions, TrustBadges } from "../constants";

const Services = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pt-28 pb-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Animated fireplace glow effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Fireplace GIF Background */}
      <div className="hidden lg:block absolute left-0 right-0 bottom-[15%] sm:bottom-[18%] md:bottom-[20%] lg:bottom-[250px] h-[45vh] sm:h-[50vh] md:h-[55vh] lg:h-[750px]">
        <img
          src={fireplaceGif}
          alt=""
          className="w-full h-full object-cover object-bottom"
        />
      </div>

      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-slate-900/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <Flame className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Professional Gas Services
            </span>
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Expert Gas Solutions{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              You Can Trust
            </span>
          </h2>
          <p className="mt-6 text-lg text-slate-300 sm:text-xl max-w-2xl mx-auto leading-relaxed">
            From installations to repairs, our certified technicians provide
            reliable gas solutions tailored to your needs.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 sm:gap-8">
          {TrustBadges.map((badge) => {
            const Icon = badge.icon;
            return (
              <div
                key={badge.text}
                className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-lg px-4 py-3 hover:border-orange-500/50 transition-colors"
              >
                <Icon className="h-6 w-6 text-orange-400" />
                <div>
                  <div className="text-sm font-semibold text-white">
                    {badge.text}
                  </div>
                  <div className="text-xs text-slate-400">{badge.subtext}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Services Grid */}
        <div className="mt-20 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ServiceDefinitions.map((service, index) => {
            const isFeatured = service.featured;
            const Icon = service.icon;

            return (
              <div
                key={service.title}
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
                className={`
                  relative group overflow-hidden rounded-2xl shadow-xl
                  transition-all duration-300 hover:scale-[1.02]
                  ${
                    isFeatured
                      ? "lg:col-span-3 bg-gradient-to-br from-orange-600 to-red-700 border-2 border-orange-400/50"
                      : "bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 hover:border-orange-500/50"
                  }
                  fade-in-up
                `}
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-orange-600/0 group-hover:from-orange-500/10 group-hover:to-orange-600/5 transition-all duration-300" />

                {isFeatured && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
                    PRIORITY
                  </div>
                )}

                <div
                  className={`relative p-8 ${
                    isFeatured
                      ? "lg:flex lg:items-center lg:gap-8"
                      : "flex flex-col items-center text-center"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${isFeatured ? "lg:ml-8" : ""}`}
                  >
                    <div
                      className={`
                      rounded-2xl p-4 inline-flex
                      ${
                        isFeatured
                          ? "bg-white/10 backdrop-blur-sm"
                          : "bg-orange-500/10"
                      }
                    `}
                    >
                      <Icon
                        className={`
                        ${
                          isFeatured
                            ? "h-16 w-16 text-white"
                            : "h-12 w-12 text-orange-400"
                        }
                      `}
                      />
                    </div>
                  </div>

                  <div
                    className={`${
                      isFeatured ? "flex-1 lg:text-left" : "mt-6"
                    } ${
                      isFeatured ? "text-center lg:text-left" : "text-center"
                    }`}
                  >
                    <h3
                      className={`
                      font-bold mb-3
                      ${
                        isFeatured
                          ? "text-2xl lg:text-3xl text-white"
                          : "text-xl text-white"
                      }
                    `}
                    >
                      {service.title}
                    </h3>
                    <p
                      className={`
                      ${
                        isFeatured ? "text-orange-50 text-lg" : "text-slate-300"
                      }
                    `}
                    >
                      {service.description}
                    </p>

                    {isFeatured && (
                      <button className="mt-6 inline-flex items-center gap-2 bg-white text-orange-600 font-semibold px-6 py-3 rounded-lg hover:bg-orange-50 transition-colors">
                        <Phone className="h-5 w-5" />
                        Call Now: (980) 540-6064
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            {/* <a
              href="request-appointment"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
            >
              temp comment
              Schedule Service
            </a> */}
            {/* <a
              href="#quote"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 border-2 border-slate-600 px-8 py-4 text-lg font-semibold text-white hover:bg-slate-700 transition-all"
            >
              temp comment
              Get Free Quote           
               </a> */}
          </div>
          <p className="mt-6 text-sm text-slate-400">
            Proudly serving residential and commercial clients since 2009
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Services;
